import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Info, AlertTriangle, AlertCircle, RefreshCw, CheckCheck, X, Clock, BellOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import type { Notification, NotificationType } from '../types/notification';

const TYPE_CONFIG: Record<NotificationType, { icon: typeof Info; colorClass: string; bgClass: string; borderClass: string; label: string; accentColor: string }> = {
  INFO: { icon: Info, colorClass: 'text-accent-1', bgClass: 'bg-accent-1/10', borderClass: 'border-l-accent-1', label: 'Informacao', accentColor: '#58a6ff' },
  UPDATE: { icon: RefreshCw, colorClass: 'text-success', bgClass: 'bg-success/10', borderClass: 'border-l-success', label: 'Atualizacao', accentColor: '#10b981' },
  WARNING: { icon: AlertTriangle, colorClass: 'text-warning', bgClass: 'bg-warning/10', borderClass: 'border-l-warning', label: 'Aviso', accentColor: '#f59e0b' },
  CRITICAL: { icon: AlertCircle, colorClass: 'text-danger', bgClass: 'bg-danger/10', borderClass: 'border-l-danger', label: 'Critico', accentColor: '#dc2626' },
};

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}m`;
}

function formatFullDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ─── Notification Detail Modal ─── */
function NotificationModal({
  notification,
  onClose,
}: {
  notification: Notification;
  onClose: () => void;
}) {
  const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.INFO;
  const Icon = config.icon;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm notif-fade-in" />

      <div className="relative w-full max-w-lg bg-bg-1 rounded-2xl shadow-2xl overflow-hidden notif-slide-up border border-border-1">
        {/* Accent bar */}
        <div className="h-1.5" style={{ backgroundColor: config.accentColor }} />

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-0">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${config.bgClass}`}>
              <Icon size={18} className={config.colorClass} />
            </div>
            <div>
              <span className={`text-[10px] font-semibold uppercase tracking-widest ${config.colorClass}`}>
                {config.label}
              </span>
              <div className="flex items-center gap-1.5 mt-0.5 text-text-3">
                <Clock size={11} />
                <span className="text-[11px]">{formatFullDate(notification.createdAt)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-bg-3 transition-colors text-text-3 hover:text-text-1"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Title */}
        <div className="px-6 pt-4">
          <h2 className="text-lg font-bold text-text-1 leading-snug">
            {notification.title}
          </h2>
        </div>

        <div className="mx-6 mt-4 border-t border-border-1" />

        {/* Message body */}
        <div className="px-6 py-5 max-h-[50vh] overflow-y-auto">
          <div className="text-sm text-text-2 leading-relaxed whitespace-pre-wrap break-words">
            {notification.message}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border-1 bg-bg-2/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium rounded-lg bg-bg-3 text-text-1 hover:bg-bg-hover transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>

      <style>{`
        .notif-fade-in {
          animation: notifFadeIn 150ms ease-out;
        }
        .notif-slide-up {
          animation: notifSlideUp 250ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .notif-dropdown-enter {
          animation: notifDropdownEnter 200ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes notifFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes notifSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes notifDropdownEnter {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

/* ─── Main NotificationBell Component ─── */
export default function NotificationBell() {
  const { accessToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!accessToken) return;
    try {
      const response = await apiClient.getUnreadNotificationCount(accessToken);
      if (response.success) {
        setUnreadCount(response.data.count);
      }
    } catch {
      // Silently ignore errors on polling
    }
  }, [accessToken]);

  const fetchNotifications = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const response = await apiClient.getNotifications(accessToken, 1, 10);
      if (response.success) {
        setNotifications(response.data.items);
      }
    } catch {
      // Silently ignore
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleMarkAsRead = async (id: string) => {
    if (!accessToken) return;
    try {
      await apiClient.markNotificationAsRead(id, accessToken);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Ignore
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!accessToken) return;
    try {
      await apiClient.markAllNotificationsAsRead(accessToken);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Ignore
    }
  };

  const handleOpenNotification = (notification: Notification) => {
    setSelectedNotification(notification);
    setIsOpen(false);
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
  };

  return (
    <>
      <div className="relative" ref={menuRef}>
        {/* Bell Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`relative p-2 rounded-xl transition-all duration-200 ${
            isOpen
              ? 'bg-accent-1/10 text-accent-1'
              : 'hover:bg-bg-3 text-text-2 hover:text-text-1'
          }`}
          aria-label="Notificacoes"
        >
          <Bell size={20} className={unreadCount > 0 ? 'animate-[notifBellShake_0.5s_ease-in-out]' : ''} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-danger rounded-full ring-2 ring-bg-2 animate-[notifPulse_2s_ease-in-out_infinite]">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-96 bg-bg-1 border border-border-1 rounded-2xl shadow-2xl z-50 overflow-hidden notif-dropdown-enter">
            {/* Header */}
            <div className="px-5 py-4 bg-bg-2/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-accent-1/10">
                    <Bell size={15} className="text-accent-1" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-text-1">Notificacoes</h3>
                    {unreadCount > 0 && (
                      <p className="text-[10px] text-text-3 mt-0.5">
                        {unreadCount} {unreadCount === 1 ? 'nao lida' : 'nao lidas'}
                      </p>
                    )}
                  </div>
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-lg text-accent-1 hover:bg-accent-1/10 transition-colors"
                  >
                    <CheckCheck size={13} />
                    Marcar todas como lidas
                  </button>
                )}
              </div>
            </div>

            <div className="border-t border-border-1" />

            {/* List */}
            <div className="max-h-[420px] overflow-y-auto">
              {loading && notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 size={24} className="text-accent-1 animate-spin" />
                  <p className="text-xs text-text-3">Carregando notificacoes...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="p-4 rounded-2xl bg-bg-3">
                    <BellOff size={28} className="text-text-3" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-text-2">Tudo em dia!</p>
                    <p className="text-xs text-text-3 mt-0.5">Nenhuma notificacao no momento</p>
                  </div>
                </div>
              ) : (
                <div className="py-1">
                  {notifications.map((notification) => {
                    const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.INFO;
                    const Icon = config.icon;
                    return (
                      <button
                        key={notification.id}
                        onClick={() => handleOpenNotification(notification)}
                        className={`w-full text-left px-4 py-3 transition-all duration-150 hover:bg-bg-hover group ${
                          !notification.isRead ? 'bg-accent-1/[0.03]' : ''
                        }`}
                      >
                        <div className={`flex gap-3 pl-3 border-l-2 ${
                          !notification.isRead ? config.borderClass : 'border-l-transparent'
                        }`}>
                          <div className={`mt-0.5 p-1.5 rounded-lg ${config.bgClass} shrink-0 transition-transform duration-150 group-hover:scale-110`}>
                            <Icon size={14} className={config.colorClass} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`text-sm leading-snug ${
                                !notification.isRead
                                  ? 'font-semibold text-text-1'
                                  : 'font-medium text-text-2'
                              }`}>
                                {notification.title}
                              </p>
                              <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                                <span className="text-[10px] text-text-3 tabular-nums">
                                  {timeAgo(notification.createdAt)}
                                </span>
                                {!notification.isRead && (
                                  <span className="w-2 h-2 rounded-full bg-accent-1 shrink-0" />
                                )}
                              </div>
                            </div>
                            <p className="text-xs text-text-3 mt-1 line-clamp-2 leading-relaxed">
                              {notification.message}
                            </p>
                            <span className={`inline-flex items-center gap-1 mt-1.5 text-[10px] font-medium uppercase tracking-wider ${config.colorClass}`}>
                              {config.label}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <>
                <div className="border-t border-border-1" />
                <div className="px-4 py-2.5 bg-bg-2/30">
                  <p className="text-center text-[10px] text-text-3">
                    Mostrando as {notifications.length} mais recentes
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedNotification && createPortal(
        <NotificationModal
          notification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
        />,
        document.body,
      )}

      {/* Keyframe animations */}
      <style>{`
        .notif-fade-in {
          animation: notifFadeIn 150ms ease-out;
        }
        .notif-slide-up {
          animation: notifSlideUp 250ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .notif-dropdown-enter {
          animation: notifDropdownEnter 200ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes notifFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes notifSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes notifDropdownEnter {
          from { opacity: 0; transform: translateY(-8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes notifBellShake {
          0%, 100% { transform: rotate(0deg); }
          15% { transform: rotate(10deg); }
          30% { transform: rotate(-8deg); }
          45% { transform: rotate(6deg); }
          60% { transform: rotate(-4deg); }
          75% { transform: rotate(2deg); }
        }
        @keyframes notifPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </>
  );
}
